import globalOptions from '../../data/globalOptions.json';

export const COUNTRIES = globalOptions.countries || [];
export const CURRENCIES = globalOptions.currencies || [];
export const TIMEZONES = globalOptions.timezones || [];
export const DATE_FORMATS = globalOptions.dateFormats || [];
export const GLOBAL_DEFAULTS = globalOptions.defaults || {};
export const INDIAN_STATES = (globalOptions.statesByCountry?.IN || []).map((item) => item.name);
export const STATES_BY_COUNTRY = globalOptions.statesByCountry || {};
export const CITIES_BY_STATE_CODE = globalOptions.citiesByStateCode || {};
export const WEEKDAYS = globalOptions.weekdays || [];

export const slugCode = (value) => String(value || '')
  .trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 64);

export const labelFor = (value) => String(value || '')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/[_-]+/g, ' ')
  .trim()
  .toLowerCase()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const parseJsonValue = (value, fallback = {}) => {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return fallback; }
};

export const jsonForEditor = (value, fallback = {}) => {
  const parsed = parseJsonValue(value, fallback);
  return JSON.stringify(parsed, null, 2);
};

export const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    const compact = [value.line1, value.line2, value.address, value.city, value.state, value.pincode || value.postal_code, value.country]
      .filter(Boolean).join(', ');
    return compact || JSON.stringify(value);
  }
  return String(value);
};

export const normalizeOptions = (rows = [], preferredKeys = ['name', 'legal_name', 'label', 'code']) =>
  (Array.isArray(rows) ? rows : []).filter(Boolean).map((row) => ({
    value: row.id,
    label: preferredKeys.map((key) => row[key]).find(Boolean) || `Record ${row.id}`,
  }));

export const normalizeAddress = (value) => {
  const parsed = parseJsonValue(value, {});
  return {
    line1: parsed.line1 || parsed.address_line1 || parsed.address || '',
    line2: parsed.line2 || parsed.address_line2 || '',
    city: parsed.city || '',
    state: parsed.state || '',
    pincode: parsed.pincode || parsed.postal_code || '',
    country: parsed.country || COUNTRIES.find((country) => country.code === GLOBAL_DEFAULTS.countryCode)?.name || '',
  };
};

const pad = (value) => String(value).padStart(2, '0');

export const formatDateTimeForPreference = (value, format = 'DD/MM/YYYY') => {
  if (!value) return '';
  const normalized = String(value).replace(' ', 'T');
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!match) return String(value);
  const [, year, month, day, hours = '00', minutes = '00'] = match;
  const date = format === 'MM/DD/YYYY' ? `${month}/${day}/${year}` : format === 'YYYY-MM-DD' ? `${year}-${month}-${day}` : `${day}/${month}/${year}`;
  return `${date} ${hours}:${minutes}`;
};

export const parsePreferredDateTime = (value, format = 'DD/MM/YYYY') => {
  if (!value) return null;
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{1,2}):(\d{2}))?$/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]} ${pad(iso[4] || 0)}:${pad(iso[5] || 0)}:00`;
  const match = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (!match) throw new Error(`Use ${format} HH:mm`);
  let year; let month; let day;
  if (format === 'MM/DD/YYYY') {
    month = Number(match[1]); day = Number(match[2]); year = Number(match[3]);
  } else {
    day = Number(match[1]); month = Number(match[2]); year = Number(match[3]);
  }
  const hours = Number(match[4] || 0); const minutes = Number(match[5] || 0);
  const date = new Date(year, month - 1, day, hours, minutes, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day || hours > 23 || minutes > 59) throw new Error(`Use a valid ${format} HH:mm value`);
  return `${year}-${pad(month)}-${pad(day)} ${pad(hours)}:${pad(minutes)}:00`;
};
